import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ComingSoonCards } from '@/components/ComingSoonCards';
import { useTeamAccess } from '@/hooks/useTeamAccess';
import { useHideOnScroll } from '@/hooks/useHideOnScroll';
import { useCart } from '@/context/CartContext';
import { useAuth } from '@/context/AuthContext';
import {
  LayoutDashboard, ShoppingBag, ClipboardList, GraduationCap, LayoutGrid, X,
  ShoppingCart, Gift, CreditCard, FileText, Camera, BookOpen, Users, BarChart3,
  Calculator, Star, Globe, ScanLine, Repeat, RefreshCw, ClipboardCheck, User,
  Bell, FileQuestion, LifeBuoy, Info, TrendingUp, LogOut, CalendarDays,
  Package, Plus, Menu as MenuIcon,
} from 'lucide-react';

/* design_lock_26oct2026: phones get a fixed bottom bar — never more than
   5 big buttons, each a picture and one word. Everything else lives in a
   "More" sheet with big tiles. Plum, cream and gold only. */

const PLUM = '#291525';
const CREAM = '#FBF8F5';
const GOLD = '#D9BE8E';

const SALON_MORE = [
  { label: 'Cart', path: '/dashboard/cart', icon: ShoppingCart },
  { label: 'Starter Packs', path: '/dashboard/starter-packs', icon: Gift },
  { label: 'Invoices', path: '/dashboard/my-salon-invoices', icon: CreditCard },
  { label: 'Quotes', path: '/dashboard/my-salon-quotes', icon: FileText },
  { label: 'Results', path: '/dashboard/results-logbook', icon: Camera },
  { label: 'Lookbook', path: '/dashboard/lookbook', icon: BookOpen },
  { label: 'My Team', path: '/dashboard/my-team', icon: Users },
  { label: 'Planner', path: '/dashboard/business-planner', icon: BarChart3 },
  { label: 'Calculator', path: '/dashboard/calculator', icon: Calculator },
  { label: 'Rewards', path: '/dashboard/rewards', icon: Star },
  { label: 'Professionals', path: '/dashboard/salon-directory', icon: Globe },
  { label: 'Scan & Reorder', path: '/dashboard/product-scanner', icon: ScanLine },
  { label: 'Auto-Restock', path: '/dashboard/subscriptions', icon: Repeat },
  { label: 'Reorder', path: '/dashboard/reorder', icon: RefreshCw },
  { label: 'Smart Restock', path: '/dashboard/reorder-intelligence', icon: TrendingUp },
  { label: 'Stock Check', path: '/dashboard/stock-check-rep', icon: ClipboardCheck },
  { label: 'Profile', path: '/dashboard/profile', icon: User },
  { label: 'Notifications', path: '/dashboard/messages', icon: Bell },
  { label: 'FAQ', path: '/dashboard/faq', icon: FileQuestion },
  { label: 'Help', path: '/dashboard/help', icon: LifeBuoy },
];

const TEAM_MORE = [
  { label: 'About', path: '/dashboard/about', icon: Info },
  { label: 'Cart', path: '/dashboard/cart', icon: ShoppingCart },
  { label: 'Quotes', path: '/dashboard/my-salon-quotes', icon: FileText, area: 'access_quotes' },
  { label: 'Invoices', path: '/dashboard/my-salon-invoices', icon: CreditCard, area: 'access_invoices_payments' },
  { label: 'Lookbook', path: '/dashboard/lookbook', icon: BookOpen },
  { label: 'Notifications', path: '/dashboard/messages', icon: Bell },
  { label: 'FAQ', path: '/dashboard/faq', icon: FileQuestion },
  { label: 'Help', path: '/dashboard/help', icon: LifeBuoy },
];

export const MobileBottomNav = ({ role, primaryOverride, moreOverride, onOpenMenu, bookingBar }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [moreOpen, setMoreOpen] = useState(false);
  const { areas } = useTeamAccess();
  const { totalItems } = useCart();
  const barsHidden = useHideOnScroll();
  const { user, logout } = useAuth();
  const isTeam = role === 'team_member'
    || (role === 'salon_staff' && !['owner', 'manager'].includes((user?.salon_role || '').toLowerCase()));
  const primary = primaryOverride || [
    { label: 'Home', path: '/dashboard', icon: LayoutDashboard },
    (!isTeam || areas.access_products) && { label: 'Products', path: '/dashboard/catalogue', icon: ShoppingBag, badge: totalItems },
    (!isTeam || areas.access_orders) && { label: 'Orders', path: isTeam ? '/dashboard/orders' : '/dashboard/my-salon-orders', icon: ClipboardList },
    (!isTeam || areas.access_academy) && { label: 'Academy', path: '/dashboard/learning', icon: GraduationCap },
  ].filter(Boolean);
  const more = moreOverride || (isTeam
    ? TEAM_MORE.filter((it) => {
      if (it.path === '/dashboard/cart') return areas.access_place_orders;
      if (it.area) return !!areas[it.area];
      return true;
    })
    : SALON_MORE);

  useEffect(() => { setMoreOpen(false); }, [location.pathname]);
  useEffect(() => {
    /* h305_fixes item 8: the plum calendar bar's ☰ inside the booking iframe
       opens the main menu via postMessage */
    const onMsg = (e) => { if (e.data && e.data.rvk === 'openMenu' && onOpenMenu) onOpenMenu(); };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, [onOpenMenu]);
  useEffect(() => {
    /* ceo_bottom_bar_27oct2026 — hide Listen/chat floats while the sheet is open */
    document.body.classList.toggle('rvk-menu-open', moreOpen);
    if (!moreOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setMoreOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.classList.remove('rvk-menu-open');
    };
  }, [moreOpen]);

  const btnStyle = (active) => ({
    flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
    justifyContent: 'center', gap: 3, minHeight: 50, border: 0, cursor: 'pointer',
    background: 'transparent', color: active ? GOLD : '#CDBFC9',
    fontWeight: active ? 700 : 500, textDecoration: 'none', position: 'relative',
  });

  return (
    <>
      {moreOpen && !onOpenMenu && (
        <div data-testid="more-sheet-backdrop" onClick={() => setMoreOpen(false)}
          style={{ position: 'fixed', inset: 0, zIndex: 80, background: 'rgba(43,22,39,.45)' }}>
          <div role="dialog" aria-label="More" data-testid="more-sheet" onClick={(e) => e.stopPropagation()}
            style={{ position: 'fixed', left: 0, right: 0, bottom: 0, width: '100%', maxWidth: 'none',
              maxHeight: '88dvh', display: 'flex', flexDirection: 'column',
              background: CREAM, borderRadius: '22px 22px 0 0',
              boxShadow: '0 -18px 50px rgba(43,22,39,.35)' }}>            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 16px 12px', flexShrink: 0 }}>
              <span style={{ fontSize: 12, letterSpacing: '2.5px', fontWeight: 700, color: '#7E5F2E', textTransform: 'uppercase' }}>More</span>
              <button type="button" aria-label="Close" data-testid="more-sheet-close"
                onClick={() => setMoreOpen(false)}
                style={{ minWidth: 44, minHeight: 44, borderRadius: 999, border: '1px solid rgba(43,22,39,.25)', background: 'transparent', color: PLUM, cursor: 'pointer', display: 'grid', placeItems: 'center' }}>
                <X size={20} />
              </button>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '0 16px 16px', WebkitOverflowScrolling: 'touch' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
                {more.map((it) => {
                  const I = it.icon;
                  return (
                    <Link key={it.path + it.label} to={it.path} data-testid={`more-tile-${it.label.toLowerCase().replace(/[&\s]+/g, '-')}`}
                      onClick={() => setMoreOpen(false)}
                      style={{ display: 'flex', alignItems: 'center', gap: 12, background: '#FFFFFF', borderRadius: 16,
                        padding: '16px 14px', textDecoration: 'none', color: PLUM, fontWeight: 600, fontSize: 15,
                        boxShadow: '0 1px 0 #E8DDD5' }}>
                      <I size={22} color="#7E5F2E" />{it.label}
                    </Link>
                  );
                })}
              </div>
              <ComingSoonCards />
            </div>
            {/* ceo_bottom_bar_27oct2026 — Log out pinned at the bottom of the
                sheet; the list above scrolls, this never moves. */}
            <div style={{ flexShrink: 0, padding: '12px 16px calc(14px + env(safe-area-inset-bottom, 0px))',
              borderTop: '1px solid #E8DDD5', background: CREAM }}>
              <button type="button" data-testid="more-sheet-logout"
                onClick={() => { setMoreOpen(false); logout(); navigate('/login'); }}
                style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                  minHeight: 48, borderRadius: 999, border: '1px solid #7A3E22', background: '#FFFFFF', color: '#7A3E22',
                  fontWeight: 600, fontSize: 16, cursor: 'pointer' }}>
                <LogOut size={18} /> Log out
              </button>
            </div>
          </div>
        </div>
      )}
      <nav aria-label="Main" data-testid="mobile-bottom-nav"
        className="flex xl:hidden"
        style={{ position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 49, background: PLUM,
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
          transition: 'transform .2s', transform: barsHidden && !moreOpen ? 'translateY(110%)' : 'none',
          boxShadow: '0 -8px 26px rgba(43,22,39,.3)' }}>
        {/* one_menu_booking_bar_02jun2026 (CEO): salons with booking ON get
            Home · Calendar · + New booking (centre) · Stock · Menu. The last
            button opens the SAME plum menu — the second "More" list is gone. */}
        {bookingBar ? (
          <>
            {[{ label: 'Home', path: '/dashboard/booking/home', icon: LayoutDashboard },
              { label: 'Calendar', path: '/dashboard/booking/cal', icon: CalendarDays }].map((it) => {
              const active = location.pathname === it.path
                || (it.path === '/dashboard/booking/cal' && location.pathname.startsWith('/dashboard/booking/cal'));
              const I = it.icon;
              return (
                <Link key={it.path} to={it.path} style={{ ...btnStyle(active), minHeight: 58 }}
                  data-testid={`bottom-nav-${it.label.toLowerCase()}`} aria-label={it.label}>
                  <I size={22} aria-hidden="true" />
                  <span style={{ fontSize: 12, lineHeight: '14px' }}>{it.label}</span>
                </Link>
              );
            })}
            <Link to="/dashboard/booking/cal:new" aria-label="New booking"
              data-testid="bottom-nav-new-booking" style={{ ...btnStyle(false), minHeight: 58 }}>
              <span aria-hidden="true" style={{ width: 48, height: 48, borderRadius: '50%',
                background: GOLD, color: PLUM, display: 'grid', placeItems: 'center',
                marginTop: -22, border: `3px solid ${PLUM}`,
                boxShadow: '0 4px 12px rgba(0,0,0,.35)' }}>
                <Plus size={26} strokeWidth={2.5} />
              </span>
              <span className="sr-only">New booking</span>
            </Link>
            <Link to="/dashboard/booking/stock" aria-label="Stock" data-testid="bottom-nav-stock"
              style={{ ...btnStyle(location.pathname === '/dashboard/booking/stock'), minHeight: 58 }}>
              <Package size={22} aria-hidden="true" />
              <span style={{ fontSize: 12, lineHeight: '14px' }}>Stock</span>
            </Link>
            <button type="button" onClick={onOpenMenu} style={{ ...btnStyle(false), minHeight: 58 }}
              data-testid="bottom-nav-menu" aria-label="Open the menu">
              <MenuIcon size={22} aria-hidden="true" />
              <span style={{ fontSize: 12, lineHeight: '14px' }}>Menu</span>
            </button>
          </>
        ) : (
          <>
            {primary.map((it) => {
              const active = location.pathname === it.path;
              const I = it.icon;
              return (
                <Link key={it.path} to={it.path} style={btnStyle(active)} data-testid={`bottom-nav-${it.label.toLowerCase()}`} aria-label={it.label}>
                  <I size={22} aria-hidden="true" />
                  {it.badge > 0 && <span aria-hidden="true" style={{ position: 'absolute', top: 5, right: '26%', background: '#C0392B', color: '#fff', fontSize: 10, fontWeight: 700, minWidth: 16, height: 16, borderRadius: 999, display: 'grid', placeItems: 'center', padding: '0 3px' }}>{it.badge}</span>}
                  <span className="sr-only">{it.label}</span>
                </Link>
              );
            })}
            {onOpenMenu ? (
              <button type="button" onClick={onOpenMenu} style={btnStyle(false)} data-testid="bottom-nav-menu" aria-label="Open the menu">
                <MenuIcon size={22} aria-hidden="true" /><span className="sr-only">Menu</span>
              </button>
            ) : (
              <button type="button" onClick={() => setMoreOpen(true)} style={btnStyle(moreOpen)} data-testid="bottom-nav-more" aria-label="More">
                <LayoutGrid size={22} aria-hidden="true" /><span className="sr-only">More</span>
              </button>
            )}
          </>
        )}
      </nav>
    </>
  );
};
